<template>
  <div class="step-container client" :class="containerClass">
    <help-drawer :title="helpTitle">
      <div>
        <h4 class="pb-2">What is a beneficiary?</h4>
        <p>
          A <strong>beneficiary</strong> can be any person or entity the owner
          chooses to receive their <strong>account assets</strong> after he
          or she dies. The total allocated to all beneficiaries must add up to
          100%.
          <a target="_blank" href="https://irs.gov">IRS.gov</a>
        </p>
        <h4 class="pb-2">What are Primary and Contingent beneficiaries?</h4>
        <p>
          A <strong>primary</strong> beneficiary will be the first in line to
          receive your assets.<br />
          <strong>Contingent beneficiaries</strong> are also important because
          they will receive your assets if none of your
          <strong>primary beneficiaries</strong>
          are alive at the time of your passing.
        </p>
      </div>
    </help-drawer>
    <v-form name="AccountBeneficiaries" class="form" data-test-id="contingent-beneficiaries-form">
      <v-container class="client-step-container beneficiary-container">
        <syncing-indicator :pageName="MENUS.CLIENT.ACCOUNT_CONTINGENT_BENEFICIARIES.id" :instance="instance"></syncing-indicator>
        <v-row class="mb-8">
          <v-col class="instructions">
            <h1 data-test-id="contingent-beneficiaries-header">
              <span class="headerlight">
                Would you like to
                <strong> add Contingent Beneficiaries </strong>
                to
                <strong>
                  {{ accountName }} ({{
                  label
                  }}) account?
                </strong>
              </span>
            </h1>
            <div class="pt-2 help-link">
              <a @click="toggleHelp()" data-test-id="help-link"> {{ helpTitle }} </a>
            </div>
          </v-col>
        </v-row>
        <data-submitted-message
          :submitted="isAccountContingentBeneficiaryComplete(instance)"
          v-on:hide-form="toggleFormState"
          data-test-id="data-submitted-message"
        />
        <div v-if="!formHidden">
          <v-row class="justify-center">
            <v-col class="step-form-container radio-container mb-4">
              <v-radio-group
                v-model="addAccountBeneficiaries"
                :error-messages="accountBeneficiaryErrors"
                hide-details="auto"
                @change="addInitialAccountBeneficiary()"
                data-test-id="add-beneficiaries-radio-group"
              >
                <v-radio
                  :ripple="false"
                  class="v-faux-radio mb-4"
                  label="Yes"
                  :value="true"
                  data-test-id="add-beneficiaries-yes"
                ></v-radio>
                <div class="expander top" v-show="addAccountBeneficiaries">
                  <!-- Alternate Beneficiary -->
                  <div
                    v-for="(beneficiary, index) in alternateBeneficiaries"
                    :key="beneficiary"
                    class="beneficiary-block"
                  >
                    <div class="remove-area">
                      <span @click="removeAlternateBeneficiary(index)" data-test-id="remove-beneficiary-button">
                        <v-icon small :color="textColor"> fas fa-trash </v-icon>
                      </span>
                    </div>
                    <v-row>
                      <v-col class="col-first-name">
                        <label for="firstName" class="required" :data-test-id="'beneficiary-' + index + '-first-name-label'">
                          First Name
                        </label>
                        <v-text-field
                          outlined
                          type="text"
                          maxlength="256"
                          name="firstName"
                          aria-required="true"
                          v-model.trim="beneficiary.firstName"
                          :backgroundColor="inputBackgroundColor"
                          @input="$v.alternateBeneficiaries.$each[index].firstName.$touch()"
                          @blur="$v.alternateBeneficiaries.$each[index].firstName.$touch()"
                          :error-messages="alternateBeneficiaryFirstNameErrors(index)"
                          hide-details="auto"
                          class="mb-2"
                          :data-test-id="'beneficiary-' + index + '-first-name-input'"
                        />
                      </v-col>
                      <v-col class="col-last-name">
                        <label for="lastName" class="required" :data-test-id="'beneficiary-' + index + '-last-name-label'">
                          Last Name
                        </label>
                        <v-text-field
                          outlined
                          type="text"
                          maxlength="256"
                          name="lastName"
                          aria-required="true"
                          v-model.trim="beneficiary.lastName"
                          :backgroundColor="inputBackgroundColor"
                          @input="$v.alternateBeneficiaries.$each[index].lastName.$touch()"
                          @blur="$v.alternateBeneficiaries.$each[index].lastName.$touch()"
                          :error-messages="alternateBeneficiaryLastNameErrors(index)"
                          hide-details="auto"
                          class="mb-2"
                          :data-test-id="'beneficiary-' + index + '-last-name-input'"
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col>
                        <label for="dob" :data-test-id="'beneficiary-' + index + '-dob-label'">Date of Birth</label>
                        <v-text-field
                          v-model="beneficiary.dob"
                          name="dob"
                          maxlength="8"
                          :placeholder="INPUT_DATE_FMT"
                          outlined
                          :backgroundColor="inputBackgroundColor"
                          @input="$v.alternateBeneficiaries.$each[index].dob.$touch()"
                          @blur="standardizeDate(index)"
                          :error-messages="alternateBeneficiaryDobErrors(index)"
                          dense
                          inputmode="numeric"
                          :data-test-id="'beneficiary-' + index + '-dob-input'"
                        >
                          <template v-slot:append>
                            <span class="cal-icon" :data-test-id="'beneficiary-' + index + '-calendar-icon'">
                              <v-icon small color="#9b9b9b">
                                fas fa-calendar-alt
                              </v-icon>
                            </span>
                          </template>
                        </v-text-field>
                      </v-col>
                      <v-col class="col-allocation-amt">
                        <label :data-test-id="'beneficiary-' + index + '-allocation-label'">Allocation Amount</label>
                        <allocation-picker
                          :value="beneficiary.allocationPercent"
                          :pickerType="PICKER_TYPES.ALLOCATION"
                          @update-number="updateAllocation($event, beneficiary)"
                          :data-test-id="'beneficiary-' + index + '-allocation-picker'"
                        ></allocation-picker>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col class="col-relation">
                        <label for="relation">Relation Status</label>
                         <div>
                          <v-select
                            v-model="beneficiary.relationStatus"
                            :items="relationStatusOptions"
                            :backgroundColor="inputBackgroundColor"
                            ref="relationStatusList"
                            outlined
                            aria-required="true"
                            placeholder="Choose Relation Status"
                            dense
                            hide-details="auto"
                            @change="$v.alternateBeneficiaries.$each[index].relationStatus.$touch()"
                            :error-messages="alternateBeneficiaryRelationStatusErrors(index)"
                            data-test-id="relation-status-select"
                          >
                          </v-select>
                      </div>
                      </v-col>
                    </v-row>
                    <div class="error-area" v-if="exceedsAllocationMax" data-test-id="allocation-error">
                      Total Allocations cannot exceed 100%
                    </div>
                  </div>
                  <div class="add-area">
                    <a @click="addAlternateBeneficiary" data-test-id="add-beneficiary-button">
                      <i class="fas fa-plus"></i>
                      Add another Contingent Beneficiary
                    </a>
                  </div>
                </div>
                <v-radio
                  :ripple="false"
                  class="v-faux-radio"
                  label="No"
                  :value="false"
                  data-test-id="add-beneficiaries-no"
                ></v-radio>
              </v-radio-group>
            </v-col>
          </v-row>

          <div class="skip-for-now">
            <a @click="skip()" data-test-id="skip-button">Skip for now</a>
          </div>
        </div>
      </v-container>
    </v-form>
    <allocation-tracker :beneficiaries="allBeneficiaries" data-test-id="allocation-tracker"></allocation-tracker>
    <bottom-nav v-on:go-prev="prev()" v-on:go-next="next()"></bottom-nav>
  </div>
</template>

<script>
import HelpDrawer from '@/components/HelpDrawerComponent';
import BottomNav from '@/components/common/BottomNav';
import DataSubmittedMessage from '@/components/client/DataSubmittedMessage';
import InterviewClientMixin from '@/common/mixins/interview.client.mixin';
import { required, requiredIf } from 'vuelidate/lib/validators';
import {
  MENUS,
  formatDate,
  US_DATE_SHORT,
  INPUT_DATE_FMT,
  validInputDate,
  PICKER_TYPES,
  ACCOUNT_TYPES,
  DB_ACCOUNT_TYPES,
  BENEFICIARY_RELATION_STATUS
} from '@/common/constants';
import AllocationPicker from '@/components/common/ValuePicker.vue';
import AllocationTracker from '@/components/client/AllocationTracker';
import SyncingIndicator from '@/components/common/SyncingIndicator.vue';

const ALLOCATION_DEFAULT = 0;

// Custom validator for allocation totals
const allocationMin = (value, vm) => {
  if (!vm.addAccountBeneficiaries || vm.allBeneficiaries.length === 0)
    return true;
  return vm.allocationTotals === 100;
};

// Custom validator for number of beneficiaries
const atLeastOneBeneficiary = (value, vm) => {
  if (!vm.addAccountBeneficiaries) return true;

  return vm.allBeneficiaries.length > 0;
};

export default {
  name: 'AccountContingentBeneficiaries',
  props: {
    name: String,
    label: String,
  },

  components: {
    BottomNav,
    HelpDrawer,
    DataSubmittedMessage,
    AllocationPicker,
    AllocationTracker,
    SyncingIndicator,
  },
  mixins: [InterviewClientMixin],

  data: () => ({
    addAccountBeneficiaries: null,
    linkedClientContingent: false,
    linkedContingentBeneficiary: {
      dob: null,
      allocationPercent: ALLOCATION_DEFAULT,
    },
    alternateBeneficiaries: [],
    dob: null,
    allocationMenuItem: 0,
    activatorMenu: false,
    dobValidationMessage: null,
    allocationDialog: false,
    targetIndex: null,
    MENUS: MENUS,
    PICKER_TYPES: PICKER_TYPES,
  }),

  validations() {
    return {
      addAccountBeneficiaries: {
        required,
        allocationMin,
        atLeastOneBeneficiary,
      },
      alternateBeneficiaries: {
        $each: {
          firstName: {
            required: requiredIf(() => this.addAccountBeneficiaries),
          },
          lastName: {
            required: requiredIf(() => this.addAccountBeneficiaries),
          },
          dob: {
            validInputDate,
          },
          allocationPercent: {},
          relationStatus: {
            required: requiredIf(() => this.addAccountBeneficiaries)
          }
        },
      },
      validationGroup: ['addAccountBeneficiaries', 'alternateBeneficiaries'],
    };
  },
  watch: {
    alternateBeneficiaries: {
      deep: true,
      handler() {
        this.alternateBeneficiaries.forEach((b) => {
          const name = `${b.firstName ? b.firstName : ''} ${
            b.lastName ? b.lastName : ''
          }`;
          this.$set(b, 'name', name);
        });
      },
    },
  },
  computed: {
    /* Dynamic errors */
    accountBeneficiaryErrors() {
      const errors = [];
      if (!this.$v.addAccountBeneficiaries.$dirty) return errors;
      !this.$v.addAccountBeneficiaries.required &&
        errors.push('A selection is required');
      !this.$v.addAccountBeneficiaries.atLeastOneBeneficiary &&
        errors.push('A beneficiary must be chosen');
      !this.$v.addAccountBeneficiaries.allocationMin &&
        errors.push('Total allocations must equal 100%');
      return errors;
    },

    allBeneficiaries() {
      const all = [];
      if (this.formHidden || !this.addAccountBeneficiaries) return all;
      this.alternateBeneficiaries.forEach((b) => all.push(b));
      return all;
    },
    helpTitle() {
      return "What's a primary & contingent beneficiary?";
    },
    accountName() {
      if (this.accountType === DB_ACCOUNT_TYPES.IRA) {
        return ACCOUNT_TYPES.IRA;
      } else if (this.accountType === DB_ACCOUNT_TYPES.ROTH_IRA) {
        return ACCOUNT_TYPES.ROTH_IRA;
      } else if (this.accountType === DB_ACCOUNT_TYPES.SEP_IRA) {
        return ACCOUNT_TYPES.SEP_IRA;
      } else if (this.accountType === DB_ACCOUNT_TYPES.SINGLE_NAME_BROKERAGE) {
        return ACCOUNT_TYPES.SINGLE_NAME_BROKERAGE;
      } else if (this.accountType === DB_ACCOUNT_TYPES.JOINT_NAME_BROKERAGE) {
        return ACCOUNT_TYPES.JOINT_NAME_BROKERAGE;
      } else if (this.accountType === DB_ACCOUNT_TYPES.OFFLINE_TRUST) {
        return 'Trust';
      } else if (this.accountType === DB_ACCOUNT_TYPES.OFFLINE_INHERITED_IRA) {
        return 'Inherited IRA';
      } else if (this.accountType === DB_ACCOUNT_TYPES.OFFLINE_SIMPLE_IRA) {
        return 'Simple IRA';
      } else if (this.accountType === DB_ACCOUNT_TYPES.OFFLINE_CORPORATE) {
        return 'Corporate';
      } else if (this.accountType === DB_ACCOUNT_TYPES.OFFLINE_OTHER) {
        return 'Other';
      } else if (this.accountType === DB_ACCOUNT_TYPES.MANAGED_IRA) {
        return ACCOUNT_TYPES.MANAGED_IRA;
      } else if (this.accountType === DB_ACCOUNT_TYPES.MANAGED_JOINT) {
        return ACCOUNT_TYPES.MANAGED_JOINT;
      } else if (this.accountType === DB_ACCOUNT_TYPES.MANAGED_ROTH_IRA) {
        return ACCOUNT_TYPES.MANAGED_ROTH_IRA;
      } else if (this.accountType === DB_ACCOUNT_TYPES.MANAGED_TRUST) {
        return ACCOUNT_TYPES.MANAGED_TRUST;
      } else if (this.accountType === DB_ACCOUNT_TYPES.MANAGED_BROKERAGE) {
        return ACCOUNT_TYPES.MANAGED_BROKERAGE;
      }
      // Default fallback for any other account types
      return this.accountType;
    },
    allocationTotals() {
      let tots = 0;
      const benes = this.allBeneficiaries || [];
      benes.forEach((b) => {
        tots += b.allocationPercent ? parseInt(b.allocationPercent) : 0;
      });
      return tots;
    },
    exceedsAllocationMax() {
      return this.allocationTotals > 100;
    },
    containerClass() {
      return this.allBeneficiaries.length > 0 ? 'has-beneficiaries' : '';
    },
    instance() {
      return { name: this.accountType, label: this.label };
    },
  },

  methods: {
    // Standardize a date from input format to US Readable
    standardizeDate(index = -1) {
      // Linked Primary
      if (index !== -1) {
        // Alternate Beneficiary
        this.$v.alternateBeneficiaries.$each[index].dob.$touch();
        if (validInputDate(this.alternateBeneficiaries[index].dob)) {
          this.alternateBeneficiaries[index].dob = this.alternateBeneficiaries[
            index
          ].dob
            ? window
                .moment(
                  this.alternateBeneficiaries[index].dob,
                  INPUT_DATE_FMT,
                  true,
                )
                .format(US_DATE_SHORT)
            : null;
        }
      }
    },

    /* Validatons for a beneficiary instance first name */
    alternateBeneficiaryFirstNameErrors(idx) {
      const errors = [];
      if (!this.$v.alternateBeneficiaries.$each[idx].firstName.$dirty)
        return errors;
      !this.$v.alternateBeneficiaries.$each[idx].firstName.required &&
        errors.push('First Name is required');
      return errors;
    },

    /* Validatons for a beneficiary instance last name */
    alternateBeneficiaryLastNameErrors(idx) {
      const errors = [];
      if (!this.$v.alternateBeneficiaries.$each[idx].lastName.$dirty)
        return errors;
      !this.$v.alternateBeneficiaries.$each[idx].lastName.required &&
        errors.push('Last Name is required');
      return errors;
    },

    /* Validatons for a beneficiary instance DOB */
    alternateBeneficiaryDobErrors(idx) {
      const errors = [];
      if (!this.$v.alternateBeneficiaries.$each[idx].dob.$dirty) return errors;
      !this.$v.alternateBeneficiaries.$each[idx].dob.validInputDate &&
        errors.push('DOB must be a valid date (MMDDYYYY)');
      return errors;
    },

    /* Validatons for a beneficiary instance Relation Status */
    alternateBeneficiaryRelationStatusErrors(idx) {
      const errors = [];
      if (!this.$v.alternateBeneficiaries.$each[idx].relationStatus.$dirty) return errors;
      !this.$v.alternateBeneficiaries.$each[idx].relationStatus.required &&
        errors.push('Relation Status is required');
      return errors;
    },

    /* Add the initial beneficiary instance */
    addInitialAccountBeneficiary() {
      this.$v.addAccountBeneficiaries.$touch();
      if (this.allBeneficiaries.length === 0) {
        this.addAlternateBeneficiary();
      }
    },

    /* Add a beneficiary instance */
    addAlternateBeneficiary() {
      this.alternateBeneficiaries.push({
        firstName: null,
        lastName: null,
        dob: null,
        allocationPercent: ALLOCATION_DEFAULT,
        activatorMenu: false,
      });
    },

    /* Remove a beneficiary instance */
    removeAlternateBeneficiary(idx) {
      this.alternateBeneficiaries.splice(idx, 1);
    },

    /* Update the allocation percentage for a beneficiary instance */
    updateAllocation(num, beneficiary) {
      beneficiary.allocationPercent = num;
    },

    /* Nav to Next Page in flow */
    next(route = null) {      
      const to = route || this.pages[this.pageIndex + 1];

      if (!this.formHidden) {
        this.$v.$touch();
        if (this.$v.$invalid || this.exceedsAllocationMax) return;
      } else {
        this.goNext(to);
        return;
      }

      const data = {
        instance: this.instance,
      };
      if (this.addAccountBeneficiaries) {
        data.beneficiaries = [];
        this.alternateBeneficiaries.forEach((b) => {
          const dobString = b.dob ? formatDate(b.dob, US_DATE_SHORT) : null;
          data.beneficiaries.push({
            firstName: b.firstName,
            lastName: b.lastName,
            dob: dobString,
            allocationAmount: b.allocationPercent,
            relationStatus: b.relationStatus
          });
        });
      }

      this.completeStep(
        MENUS.CLIENT.ACCOUNT_CONTINGENT_BENEFICIARIES.id,
        data,
        this.instance,
      )
        .then(() => {
          this.goNext(to);
        })
        .catch(() => {});
    },

    /* Nav to Previous Page in flow */
    prev() {
      this.goPrev(this.pages[this.pageIndex - 1]);
    },

    /* Skip to next Page in flow */
    skip() {      
      this.goNext(this.pages[this.pageIndex + 1]);
    },

    /* Navigate from page */
    leave() {
      const route = this.leaveRoute;

      let data = {
        instance: this.instance,
      };
      if (this.addAccountBeneficiaries) {
        data.beneficiaries = [];
        // Security: Co-applicant pre-fill removed
        // Users must manually enter all beneficiary details

        this.alternateBeneficiaries.forEach((b) => {
          const dobString = b.dob ? formatDate(b.dob, US_DATE_SHORT) : null;
          data.beneficiaries.push({
            firstName: b.firstName,
            lastName: b.lastName,
            dob: dobString,
            allocationAmount: b.allocationPercent,
          });
        });
      }

      // Mark page as syncing immediately for better user feedback
      this.markPageAsSyncing(MENUS.CLIENT.ACCOUNT_CONTINGENT_BENEFICIARIES.id, this.instance);

      this.completeStep(
        MENUS.CLIENT.ACCOUNT_CONTINGENT_BENEFICIARIES.id,
        data,
        this.instance,
      )
        .then(() => {
          this.goNext(route);
        })
        .catch(() => {});
    },
  },

  created() {
    this.pages = this.interviewPages.getInterviewPages();
    this.pageIndex;
    let i = 0;
    for (const page of this.pages) {
      if (typeof page === 'object' && window.location.pathname.includes(page.pathname.replaceAll(' ', '%20'))) {
        this.pageIndex = i;
      }
      i++;
    }
    // Initialize form data for current interview
    this.accountType = this.name;
    this.PICKER_TYPES = PICKER_TYPES;
    this.INPUT_DATE_FMT = INPUT_DATE_FMT;
    this.formHidden = this.isAccountContingentBeneficiaryComplete(
      this.instance,
    );

    // Initialize relation status options
    this.relationStatusOptions = [];
    Object.keys(BENEFICIARY_RELATION_STATUS).map((key) => {
      const val = BENEFICIARY_RELATION_STATUS[key];    
      this.relationStatusOptions.push(val);
    });
  },
};
</script>
<style lang="scss" scoped>
.cal-icon {
  line-height: 32px;
  margin-right: 8px;
}
.expander {
  display: flex;
  flex-direction: column;

  &.top {
    margin-bottom: 20px;
    padding-bottom: 20px;
  }
  .row {
    margin: -12px 0;

    @media screen and (max-width: 960px) {
      margin: -12px;
    }
  }

  .cb-label {
    margin-top: 5px;
  }

  .col {
    padding: 12px 0;

    &.col-first-name,
    &.col-dob {
      padding-right: 6px;
    }
    &.col-last-name,
    &.col-allocation-amt {
      padding-left: 6px;
    }

    &.col-relation {
      padding-top: 0;
    }

    @media screen and (max-width: 960px) {
      padding: 12px;
    }
  }
}
.half-width {
  width: calc(50% - 4px);
  overflow: hidden;
  margin: 0 0 0 4px !important;

  &.first {
    margin: 0 4px 0 0 !important;
  }
}
.has-beneficiaries {
  height: calc(100vh - 242px);
}
.beneficiary-container {
  padding-bottom: 60px;
}
.beneficiary-block {
  margin-top: 15px;
  padding-top: 10px;
  padding-bottom: 20px;
  border-top: 1px solid var(--secondary-color);
}
.add-area {
  margin-top: 10px;
}
.remove-area {
  display: flex;
  justify-content: flex-end;
}
</style>
